'use client';

import type { Reference } from '@eliadmin/core/src/api/references/references-types';

import { fetchReferences } from '@eliadmin/core/src/api/references/references-api';
import { useInfiniteQuery } from '@tanstack/react-query';
import { cn } from '@eliadmin/core/src/utils/style-utils';

import { Button } from '@eliadmin/core/src/components/ui/button';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { ReferenceCard } from './reference-card';
import { ReferenceSkeletonStructure } from './reference-skeleton-structure';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

export function ReferencesList({ locale }: { locale: string }) {
  const { t } = useTranslations();
  const referencesQuery = useInfiniteQuery({
    queryKey: [CacheKey.REFERENCES, locale],
    queryFn: async ({ pageParam = 1 }) => {
      return await fetchReferences({
        locale,
        query: {
          page: pageParam,
          results: 6,
        },
      });
    },
    getNextPageParam: (lastPage: any) => {
      if (lastPage?.meta?.current_page < lastPage?.meta?.last_page)
        return lastPage?.meta?.current_page + 1;
      return undefined;
    },
    initialPageParam: 1,
  });

  const references = referencesQuery.data?.pages.flatMap((page: any) => page.data) ?? [];

  return (
    <>
      <section className={cn('custom-section', 'bg-[#F1F6F8] pb-16 pt-8')}>
        {referencesQuery.error && <p className="pt-8 text-center">{t('text2364')}</p>}
        {referencesQuery.isLoading && <ReferenceSkeletonStructure />}
        {referencesQuery.isSuccess && references.length > 0 && (
          <div
            className={cn(
              'custom-container',
              'grid grid-cols-1 gap-6 sm:grid-cols-2 md:gap-8 lg:grid-cols-3',
            )}
          >
            {references.map(reference => (
              <ReferenceCard key={reference.name} reference={reference} />
            ))}
          </div>
        )}
        <div
          className={cn('mt-8 flex w-full items-center justify-center', {
            hidden: !referencesQuery.hasNextPage,
          })}
        >
          <Button
            onClick={() => referencesQuery.fetchNextPage()}
            isLoading={referencesQuery.isFetchingNextPage}
            className="w-full sm:w-fit"
            variant={'dark'}
          >
            {t('text338')}
          </Button>
        </div>
      </section>
    </>
  );
}
